'use client';

import { useRouter } from 'next/navigation';
import { useMemo } from 'react';

import { InlineButton } from '@web/ui';

import styles from './AuthForm.module.scss';
import { LoginForm } from './LoginForm';
import { RegisterForm } from './RegisterForm';

import type { AuthMode } from '@/features/auth/types';

const tabContent: Record<
  AuthMode,
  { title: string; subtitle: string; cta: string }
> = {
  login: {
    title: 'Log in to your account',
    subtitle: 'Welcome back! Please enter your details.',
    cta: 'Sign in',
  },
  signup: {
    title: 'Create your account',
    subtitle: "Let's get you set up.",
    cta: 'Create account',
  },
};

const authModeRoutes: Record<AuthMode, string> = {
  login: '/auth/login',
  signup: '/auth/signup',
};

type AuthFormProps = {
  mode?: AuthMode;
  inviteCode?: string;
  invitedEmail?: string;
  termsMarkdownContent?: string;
};

export function AuthForm({
  mode = 'login',
  inviteCode = '',
  invitedEmail = '',
  termsMarkdownContent = '',
}: AuthFormProps) {
  const router = useRouter();

  const activeContent = tabContent[mode];
  const trimmedInviteCode = inviteCode.trim();
  const trimmedInvitedEmail = invitedEmail.trim();

  const buildAuthModeRoute = (nextMode: AuthMode) => {
    if (!trimmedInviteCode && !trimmedInvitedEmail) {
      return authModeRoutes[nextMode];
    }

    const params = new URLSearchParams();
    if (trimmedInviteCode) {
      params.set('invite_code', trimmedInviteCode);
    }
    if (trimmedInvitedEmail) {
      params.set('email', trimmedInvitedEmail);
    }

    return `${authModeRoutes[nextMode]}?${params.toString()}`;
  };

  const switchMode = (nextMode: AuthMode) => {
    if (nextMode === mode) {
      return;
    }

    router.push(buildAuthModeRoute(nextMode));
  };

  const bottomSwitchCopy = useMemo(() => {
    if (mode === 'login') {
      return {
        text: "Don't have an account?",
        action: 'Sign up',
        next: 'signup',
      } as const;
    }

    return {
      text: 'Already have an account?',
      action: 'Log in',
      next: 'login',
    } as const;
  }, [mode]);

  return (
    <div
      className={styles.card}
      style={{ marginTop: mode === 'login' ? '0' : '1.5rem' }}
    >
      <div className={styles.tabList} role="tablist" aria-label="Auth tabs">
        {(['login', 'signup'] as AuthMode[]).map((tab) => (
          <button
            key={tab}
            aria-label={tab === 'login' ? 'Log in' : 'Sign up'}
            onClick={() => switchMode(tab)}
            className={styles.tabButton}
            style={{
              backgroundColor:
                mode === tab ? 'var(--nn-color-primary)' : 'transparent',
              color: mode === tab ? 'var(--nn-white)' : 'var(--nn-color-text)',
              borderRadius: '999px',
            }}
          >
            {tab === 'login' ? 'Log in' : 'Sign up'}
          </button>
        ))}
      </div>

      <div className={styles.header}>
        <h1>{activeContent.title}</h1>
        <p>
          {trimmedInviteCode
            ? mode === 'login'
              ? 'Log in with the invited email to join this workspace.'
              : 'Create your account with the invited email to join this workspace.'
            : activeContent.subtitle}
        </p>
      </div>

      {mode === 'login' ? (
        <LoginForm
          inviteCode={trimmedInviteCode}
          invitedEmail={trimmedInvitedEmail}
        />
      ) : (
        <RegisterForm
          inviteCode={trimmedInviteCode}
          invitedEmail={trimmedInvitedEmail}
          termsMarkdownContent={termsMarkdownContent}
        />
      )}

      {/* <div className={styles.divider}>
        <span />
        <p>OR</p>
        <span />
      </div> */}

      {/* <div className={styles.socialLogin}>Social Login Coming Soon...</div> */}

      <div className={styles.switchCopy}>
        {bottomSwitchCopy.text}

        <InlineButton onClick={() => switchMode(bottomSwitchCopy.next)} center>
          {bottomSwitchCopy.action}
        </InlineButton>
      </div>
    </div>
  );
}
